import { Bell, Check, CheckCheck } from "lucide-react";
import { Link } from "react-router";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useNotifications,
  type NotificationRow,
} from "@/hooks/useNotifications";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function NotificationList({
  items,
  emptyText,
  onMarkRead,
}: {
  items: NotificationRow[];
  emptyText: string;
  onMarkRead: (id: string) => void;
}) {
  if (!items.length) {
    return (
      <p className="text-xs text-muted-foreground py-8 text-center">
        {emptyText}
      </p>
    );
  }
  return (
    <ul className="divide-y max-h-[360px] overflow-y-auto">
      {items.map((n) => {
        const Inner = (
          <div className="flex items-start gap-2 p-3 hover:bg-accent/60 transition-colors">
            <span
              className={cn(
                "h-2 w-2 mt-1.5 rounded-full flex-shrink-0",
                n.read_at ? "bg-transparent" : "bg-primary",
              )}
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground line-clamp-2">
                {n.title}
              </p>
              {n.body && (
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {n.body}
                </p>
              )}
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {timeAgo(n.created_at)}
              </p>
            </div>
            {!n.read_at && (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onMarkRead(n.id);
                }}
                className="text-muted-foreground hover:text-foreground p-1"
                aria-label="Mark as read"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
        return (
          <li key={n.id}>
            {n.link ? (
              <Link
                to={n.link}
                onClick={() => onMarkRead(n.id)}
                className="block"
              >
                {Inner}
              </Link>
            ) : (
              Inner
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function NotificationsBell() {
  const { user } = useAuth();
  const { items, unreadCount, markRead, markAllRead } = useNotifications();
  const unreadItems = items.filter((n) => !n.read_at);
  if (!user) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="relative h-9 w-9 flex items-center justify-center rounded-full text-foreground/70 hover:text-foreground hover:bg-accent transition-colors"
          aria-label={`Notifications (${unreadCount} unread)`}
        >
          <Bell className="h-[18px] w-[18px]" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center animate-scale-in">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[340px] p-0 z-[80]">
        <div className="flex items-center justify-between p-3 border-b">
          <span className="text-sm font-semibold">Notifications</span>
          <button
            onClick={() => markAllRead()}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            <CheckCheck className="h-3 w-3" /> Mark all read
          </button>
        </div>
        <Tabs defaultValue="all">
          <TabsList className="w-full rounded-none bg-muted/30 grid grid-cols-2">
            <TabsTrigger value="all">All ({items.length})</TabsTrigger>
            <TabsTrigger value="unread">
              Unread ({unreadItems.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="all" className="m-0">
            <NotificationList
              items={items}
              emptyText="You're all caught up."
              onMarkRead={markRead}
            />
          </TabsContent>
          <TabsContent value="unread" className="m-0">
            <NotificationList
              items={unreadItems}
              emptyText="Nothing unread. Nice."
              onMarkRead={markRead}
            />
          </TabsContent>
        </Tabs>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
